// SOFTROOM-CATALOG-B2B-JS v1
(() => {
  const VAT_RATE = 1.21;

  class FabricConfigurator {
    constructor(root) {
      this.root = root;
      this.form = root.closest('form');
      this.mount = root.querySelector('[data-configurator-mount]');
      this.propertiesTarget = root.querySelector('[data-configurator-properties]');
      this.modal = root.querySelector('[data-fabric-modal]');
      this.modalFrame = root.querySelector('[data-fabric-modal-frame]');
      this.productForm = root.closest('product-form');
      this.variantInput = this.form ? this.form.querySelector('.product-variant-id, input[name="id"]') : null;
      this.submitButton = this.form ? this.form.querySelector('button[type="submit"][name="add"], button[type="submit"]') : null;
      this.currencyCode = root.dataset.currencyCode || 'EUR';
      const requestedLocale = String(root.dataset.locale || document.documentElement.lang || 'en').toLowerCase().split('-')[0];
      this.locale = ['en', 'es', 'ru'].includes(requestedLocale) ? requestedLocale : 'en';
      this.priceFactor = Number.parseFloat(root.dataset.priceFactor || '1') || 1;
      this.cartUrl = root.dataset.cartUrl || '/cart';
      this.sectionId = root.dataset.sectionId || '';
      this.productId = root.dataset.productId || '';
      this.initialized = false;

      this.options = [];
      this.variants = [];
      this.variantsById = new Map();
      this.popupMap = {};
      this.selectedValues = new Map();
      this.surchargeVariantIds = this.parseSurchargeMap(root.dataset.surchargeMap);

      this.previewPopover = null;
      this.previewPopoverImg = null;
      this.previewPopoverLabel = null;
      this.activePreviewButton = null;
    }

    init() {
      if (this.initialized || !this.form || !this.mount) return;

      const rawOptions = this.parseJSON(this.root.querySelector('[data-open-cart-options]'));
      const rawVariants = this.parseJSON(this.root.querySelector('[data-product-variants]'));
      const rawPopupMap = this.parseJSON(this.root.querySelector('[data-popup-map]'));

      this.popupMap = rawPopupMap && typeof rawPopupMap === 'object' ? rawPopupMap : {};
      this.variants = Array.isArray(rawVariants) ? rawVariants : [];
      this.variantsById = new Map(this.variants.map((variant) => [String(variant.id), variant]));
      this.labels = this.parseJSON(this.root.querySelector('[data-configurator-labels]')) || {};
      this.surchargePrices = new Map();
      const surchargeProducts = this.parseJSON(this.root.querySelector('[data-surcharge-products]')) || [];
      surchargeProducts.forEach(variants => {
        if (Array.isArray(variants)) variants.forEach(variant => this.surchargePrices.set(String(variant.id), Number(variant.price)));
      });
      this.options = this.normalizeOptions(rawOptions);

      if (!this.options.length) { this.root.dataset.nativeOnly = 'true'; this.root.dataset.initialized = 'true'; this.root.hidden = true; return; }

      this.render();
      this.initializeSelections();
      this.cachePreviewElements();
      this.bindEvents();
      this.updatePropertiesInputs();
      this.updateDisplayedTotals();

      this.initialized = true;
      this.root.dataset.initialized = 'true';
    }

    parseJSON(node) {
      if (!node) return null;
      const raw = (node.textContent || '').trim();
      if (!raw) return null;

      try {
        return JSON.parse(raw);
      } catch (error) {
        console.error('Fabric configurator JSON parse failed:', error, raw);
        return null;
      }
    }

    parseSurchargeMap(raw) {
      let parsed = {};
      if (typeof raw === 'string' && raw.trim()) {
        try {
          parsed = JSON.parse(raw);
        } catch (error) {
          console.warn('Failed to parse surcharge map from data attribute.', error);
        }
      }

      const normalized = {};
      Object.entries(parsed || {}).forEach(([key, value]) => {
        const cents = parseInt(String(key).replace(/[^\d-]/g, ''), 10);
        const variantId = parseInt(String(value).replace(/[^\d-]/g, ''), 10);

        if (Number.isFinite(cents) && Number.isFinite(variantId) && cents > 0 && variantId > 0) {
          normalized[cents] = variantId;
        }
      });

      return normalized;
    }

    normalizeOptions(raw) {
      if (!raw) return [];

      let list = [];
      if (Array.isArray(raw)) list = raw;
      else if (Array.isArray(raw.options)) list = raw.options;
      else if (Array.isArray(raw.product_options)) list = raw.product_options;
      else if (Array.isArray(raw.open_cart_options)) list = raw.open_cart_options;

      const variantOptionNames = new Set();

      this.variants.forEach((variant) => {
        [variant.option1, variant.option2, variant.option3].forEach((value) => {
          if (value && String(value).trim() !== '') {
            variantOptionNames.add(String(value).trim().toLowerCase());
          }
        });
      });

      const productOptionNames = new Set();
      const productOptionContainer = document.querySelector(`#MainProduct-${this.sectionId}`) || document;

      productOptionContainer
        .querySelectorAll('.product-form__input .form__label, .product-form__input legend')
        .forEach((node) => {
          const txt = (node.textContent || '').replace(':', '').trim().toLowerCase();
          if (txt) productOptionNames.add(txt);
        });

      return list
        .map((option, index) => this.normalizeOption(option, index))
        .filter((option) => option.values.length > 0)
        .filter((option) => {
          const optionName = String(option.name || '').trim().toLowerCase();
          if (!optionName) return false;

          if (this.variants.length > 1 && productOptionNames.has(optionName)) return false;

          const optionHasVariantLikeValues = option.values.some((value) => {
            const label = String(value.label || '').trim().toLowerCase();
            return label && variantOptionNames.has(label);
          });

          if (this.variants.length > 1 && optionHasVariantLikeValues && option.values.length <= 10) return false;

          return true;
        });
    }

    localizedName(item) {
      const names = item && item.names;
      if (!names || typeof names !== 'object' || Array.isArray(names)) return '';
      const localized = names[this.locale] || names.en;
      return typeof localized === 'string' ? localized.trim() : '';
    }

    normalizeOption(option, index) {
      const name = this.cleanText(
        this.firstDefined([this.localizedName(option), option.name, option.option_name, option.title, option.label]) || `Option ${index + 1}`
      );

      const rawValues =
        this.firstDefined([option.values, option.option_values, option.items, option.children]) || [];

      const values = Array.isArray(rawValues)
        ? rawValues.map((value, valueIndex) => this.normalizeValue(value, valueIndex)).filter(Boolean)
        : [];

      return {
        id: this.firstDefined([option.id, option.option_id, option.product_option_id, `option-${index}`]),
        name,
        values
      };
    }

    normalizeValue(value, index) {
      const rawLabel = this.firstDefined([
        value.name,
        value.label,
        value.title,
        value.text,
        value.value,
        value.option_value_name,
        value.html
      ]);

      const image = this.normalizeFabricImageUrl(
        this.firstDefined([
          value.image,
          value.image_url,
          value.image_src,
          value.thumb,
          value.swatch,
          value.icon
        ]) || ''
      );

      const rawSurcharge = this.extractSurcharge(value, rawLabel);
      const rawSurchargeCents = Math.round(rawSurcharge * 100);
      let baseSurchargeCents = this.applyVatToCents(rawSurchargeCents);
      // The imported net amounts have two decimals. Resolve a unique one-cent tax-rounding difference against existing support prices.
      if (baseSurchargeCents > 0 && !this.surchargeVariantIds[baseSurchargeCents]) {
        const nearby = Object.keys(this.surchargeVariantIds).map(Number).filter(cents => Math.abs(cents - baseSurchargeCents) <= 1);
        if (nearby.length === 1) baseSurchargeCents = nearby[0];
      }
      const surchargeId = this.surchargeVariantIds[baseSurchargeCents];
      const contextualPrice = surchargeId && this.surchargePrices.get(String(surchargeId));
      const surchargeCents = Number.isFinite(contextualPrice) ? contextualPrice : this.applyPriceFactorToCents(baseSurchargeCents);
      const surcharge = surchargeCents / 100;
      const label = this.cleanOptionLabel(this.localizedName(value) || rawLabel);

      const optionValueId = this.firstDefined([
        value.id,
        value.option_value_id,
        value.product_option_value_id,
        `value-${index}`
      ]);

      return {
        id: String(optionValueId),
        label: label || `Value ${index + 1}`,
        rawLabel: rawLabel || '',
        image,
        rawSurcharge,
        rawSurchargeCents,
        surcharge,
        surchargeCents,
        baseSurchargeCents,
        surchargeVariantId: baseSurchargeCents > 0 ? this.surchargeVariantIds[baseSurchargeCents] || null : null,
        isDefault: Boolean(
          value.default === true ||
          value.selected === true ||
          value.is_default === true ||
          value.isDefault === true
        )
      };
    }

    normalizeFabricImageUrl(url) {
      if (!url) return '';

      let normalized = String(url)
        .trim()
        .replace(/&amp;/g, '&')
        .replace(/\\\//g, '/');

      normalized = normalized.replace(
        /^https?:\/\/(?:www\.)?(?:skandimebeles\.lv|skandimoobel\.ee|skandibaldai\.lt)(https?:\/\/cdn\.shopify\.com\/)/i,
        '$1'
      );

      normalized = normalized.replace(
        /^https?:\/\/(?:www\.)?(?:skandimebeles\.lv|skandimoobel\.ee|skandibaldai\.lt)(\/\/cdn\.shopify\.com\/)/i,
        'https:$1'
      );

      if (/^https?:\/\//i.test(normalized)) {
        return normalized;
      }

      if (normalized.startsWith('//')) {
        return `https:${normalized}`;
      }

      if (/^(\/)?(image|catalog|download|system\/storage|storage)\//i.test(normalized)) {
        return `https://softroom.es/${normalized.replace(/^\/+/, '')}`;
      }

      return normalized;
    }

    applyVatToCents(netCents) {
      if (!netCents || netCents <= 0) return 0;
      return Math.round(netCents * VAT_RATE);
    }

    applyPriceFactorToCents(baseCents) {
      if (!baseCents || baseCents <= 0) return 0;
      return Math.round(baseCents * this.priceFactor);
    }

    extractSurcharge(value, rawLabel) {
      const numericCandidates = [
        value.price_delta,
        value.surcharge,
        value.option_surcharge,
        value.price_modifier,
        value.price_extra
      ];

      for (const candidate of numericCandidates) {
        const parsed = this.parseNumeric(candidate);
        if (parsed > 0) return parsed;
      }

      if (String(value.price_prefix || '').trim() === '+') {
        const prefixed = this.parseNumeric(
          this.firstDefined([value.price, value.option_price, value.price_value])
        );
        if (prefixed > 0) return prefixed;
      }

      const textCandidates = [
        value.price_html,
        value.option_price_html,
        value.option_price,
        value.price,
        rawLabel,
        value.html
      ];

      for (const candidate of textCandidates) {
        const parsed = this.parseSurchargeFromText(candidate);
        if (parsed > 0) return parsed;
      }

      return 0;
    }

    parseNumeric(value) {
      if (typeof value === 'number' && Number.isFinite(value)) return value;
      if (typeof value !== 'string') return 0;

      const cleaned = value.replace(/[^\d.,-]/g, '').replace(',', '.').trim();
      const parsed = parseFloat(cleaned);
      return Number.isFinite(parsed) ? parsed : 0;
    }

    parseSurchargeFromText(value) {
      if (typeof value !== 'string' || !value.trim()) return 0;

      const text = value.replace(/\s+/g, ' ').trim();
      const patterns = [
        /(?:\(\s*\+|\+)\s*([0-9]+(?:[.,][0-9]{1,2})?)\s*(?:€|eur)?\s*\)?/i,
        /option-price[^>]*>\s*\(\+\s*([0-9]+(?:[.,][0-9]{1,2})?)\s*(?:€|eur)?\s*\)\s*</i
      ];

      for (const pattern of patterns) {
        const match = text.match(pattern);
        if (match && match[1]) {
          const parsed = parseFloat(match[1].replace(',', '.'));
          if (Number.isFinite(parsed) && parsed > 0) return parsed;
        }
      }

      return 0;
    }

    cleanOptionLabel(value) {
      if (value == null) return '';

      const stripped = this.stripHtml(String(value));
      const withoutPrice = stripped
        .replace(/\(\s*\+\s*[0-9]+(?:[.,][0-9]{1,2})?\s*(?:€|eur)?\s*\)/gi, '')
        .replace(/\+\s*[0-9]+(?:[.,][0-9]{1,2})?\s*(?:€|eur)?/gi, '')
        .trim();

      return withoutPrice.replace(/\s+/g, ' ').trim();
    }

    cleanText(value) {
      return this.stripHtml(String(value || '')).replace(/\s+/g, ' ').trim();
    }

    stripHtml(value) {
      const div = document.createElement('div');
      div.innerHTML = value;
      return (div.textContent || div.innerText || '').trim();
    }

    firstDefined(values) {
      for (const value of values) {
        if (value !== undefined && value !== null && value !== '') return value;
      }
      return null;
    }

    escapeHtml(value) {
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }

    getPreferredDefaultValue(option) {
      if (!option || !Array.isArray(option.values) || !option.values.length) return null;

      const explicitDefault = option.values.find((value) => value.isDefault);
      if (explicitDefault) return explicitDefault;

      const zeroSurcharge = option.values.find((value) => Number(value.surchargeCents || 0) === 0);
      if (zeroSurcharge) return zeroSurcharge;

      const withoutLike = option.values.find((value) => {
        const text = String(value.label || '').trim().toLowerCase();
        return text.includes('without') || text.includes('none') || text.includes('bez');
      });
      if (withoutLike) return withoutLike;

      return option.values[0];
    }

    getShopifyPageUrl(path) {
      return `https://softroom.es${path}`;
    }

    normalizeVendorName(value) {
      return String(value || '')
        .replace(/\u00A0/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .toUpperCase();
    }

    getProductVendor() {
      if (this.root.dataset.productVendor) {
        return this.root.dataset.productVendor;
      }

      const productContainer =
        document.querySelector(`#MainProduct-${this.sectionId}`) ||
        this.root.closest('.product') ||
        document;

      const vendorNode =
        productContainer.querySelector('[data-product-vendor]') ||
        productContainer.querySelector('.product__vendor') ||
        productContainer.querySelector('.product-meta__vendor') ||
        productContainer.querySelector('[itemprop="brand"]') ||
        document.querySelector('[data-product-vendor]') ||
        document.querySelector('.product__vendor') ||
        document.querySelector('.product-meta__vendor') ||
        document.querySelector('[itemprop="brand"]');

      if (vendorNode && vendorNode.textContent) {
        return vendorNode.textContent;
      }

      const jsonLdNodes = document.querySelectorAll('script[type="application/ld+json"]');

      for (const node of jsonLdNodes) {
        try {
          const data = JSON.parse(node.textContent || '{}');
          const items = Array.isArray(data) ? data : [data];

          for (const item of items) {
            if (!item || item['@type'] !== 'Product') continue;

            if (typeof item.brand === 'string') return item.brand;
            if (item.brand && typeof item.brand.name === 'string') return item.brand.name;
            if (typeof item.manufacturer === 'string') return item.manufacturer;
            if (item.manufacturer && typeof item.manufacturer.name === 'string') return item.manufacturer.name;
          }
        } catch (error) {
          continue;
        }
      }

      return '';
    }

    isSoftNordVendor() {
      return this.normalizeVendorName(this.getProductVendor()) === 'SOFT NORD';
    }

    normalizeOptionMatchName(value) {
      return String(value || '')
        .replace(/\u00A0/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .toUpperCase();
    }

    hasImportedOption(match) {
      const normalizedMatch = this.normalizeOptionMatchName(match);

      const importedOptionMatch = this.options.some((option) => {
        return this.normalizeOptionMatchName(option.name) === normalizedMatch;
      });

      if (importedOptionMatch) return true;

      const variantOptionMatch = this.variants.some((variant) => {
        return [variant.option1, variant.option2, variant.option3].some((value) => {
          return this.normalizeOptionMatchName(value) === normalizedMatch;
        });
      });

      if (variantOptionMatch) return true;

      const productContainer =
        document.querySelector(`#MainProduct-${this.sectionId}`) ||
        this.root.closest('.product') ||
        document;

      const productText = this.normalizeOptionMatchName(productContainer.textContent || '');

      return productText.includes(normalizedMatch);
    }


    getSamplesConfig() {
      if (!this.isSoftNordVendor()) return { url: this.getShopifyPageUrl('/pages/fabric-samples-delivery') };

      return {
        match: 'SOFT NORD VENDOR',
        url: this.getShopifyPageUrl('/pages/order-free-fabric-samples-from-soft-nord')
      };
    }

    hasConformConfigurator() {
      return this.hasImportedOption('CHAIR MATERIAL (CONFORM)');
    }

    render() {
      const samplesConfig = this.getSamplesConfig();
      const showConformConfigurator = this.hasConformConfigurator();

      const html = [];

      html.push('<div class="fabric-configurator__inner">');
      html.push('<div class="fabric-configurator__header">');
      html.push('<div class="fabric-configurator__top-actions">');


      if (samplesConfig) {
        html.push(
          '<button type="button" class="fabric-configurator__action-button" data-open-samples="' + this.escapeHtml(samplesConfig.url) + '">' +
            '<img src="https://cdn.shopify.com/s/files/1/1063/2704/8529/files/audumi-icon.png?v=1784697445" alt="" loading="lazy" width="22" height="22">' +
            '<span>' + this.escapeHtml(this.labels.samples || 'Free fabric sample delivery') + '</span>' +
          '</button>'
        );
      }

      if (showConformConfigurator) {
        html.push(
          '<button type="button" class="fabric-configurator__action-button" data-open-conform-configurator>' +
            '<img src="https://cdn.shopify.com/s/files/1/1063/2704/8529/files/conform-config.png?v=1784697445" alt="" loading="lazy" width="22" height="22">' +
            '<span>' + this.escapeHtml(this.labels.conform || 'CONFORM chair configurator') + '</span>' +
          '</button>'
        );
      }

      html.push('</div>');

      html.push('<h3 class="fabric-configurator__title">' + this.escapeHtml(this.labels.configuration || 'Choose your configuration') + '</h3>');
      html.push('<div class="fabric-configurator__total">');
      html.push('<span class="fabric-configurator__total-label">' + this.escapeHtml(this.labels.total || 'Total with selected options:') + '</span>');
      html.push('<strong class="fabric-configurator__total-price" data-configurator-total-price></strong>');
      html.push('</div>');
      html.push('</div>');

      this.options.forEach((option) => {
        html.push('<div class="fabric-configurator__group" data-option-group="' + this.escapeHtml(option.id) + '">');
        html.push('<div class="fabric-configurator__group-head">');
        html.push('<div class="fabric-configurator__group-title">' + this.escapeHtml(option.name) + '</div>');
        html.push('</div>');
        html.push('<div class="fabric-configurator__grid">');

        option.values.forEach((value, index) => {
          const imageUrl = this.normalizeFabricImageUrl(value.image);

          const surchargeBadge = value.surcharge > 0
            ? '<span class="fabric-configurator__badge">(' + this.escapeHtml(this.formatEuroBadge(value.surcharge)) + ')</span>'
            : '';

          const previewAttrs = imageUrl
            ? ' data-preview-image="' + this.escapeHtml(imageUrl) + '" data-preview-label="' + this.escapeHtml(value.label) + '"'
            : '';

          html.push(
            '<button type="button" class="fabric-configurator__swatch" data-option-id="' +
              this.escapeHtml(option.id) +
              '" data-value-id="' +
              this.escapeHtml(value.id) +
              '" aria-pressed="false"' +
              previewAttrs +
            '>'
          );

          html.push('<span class="fabric-configurator__swatch-inner">');

          if (imageUrl) {
            html.push(
              '<span class="fabric-configurator__image-wrap">' +
                '<img class="fabric-configurator__image" src="' +
                this.escapeHtml(imageUrl) +
                '" alt="' +
                this.escapeHtml(value.label) +
                '" loading="' + (index < 12 ? 'eager' : 'lazy') + '" decoding="async">' +
              '</span>'
            );
          }

          html.push('<span class="fabric-configurator__label">' + this.escapeHtml(value.label) + '</span>');
          html.push(surchargeBadge);
          html.push('</span>');
          html.push('</button>');
        });

        html.push('</div>');
        html.push('</div>');
      });

      html.push(
        '<div class="fabric-configurator__preview-popover" hidden data-fabric-preview-popover>' +
          '<button type="button" class="fabric-configurator__preview-close" aria-label="Close preview" data-fabric-preview-close>×</button>' +
          '<div class="fabric-configurator__preview-image-wrap">' +
            '<img class="fabric-configurator__preview-image" src="" alt="" data-fabric-preview-image>' +
          '</div>' +
          '<div class="fabric-configurator__preview-label" data-fabric-preview-label></div>' +
        '</div>'
      );

      html.push('</div>');
      this.mount.innerHTML = html.join('');
    }

    cachePreviewElements() {
      this.previewPopover = this.mount.querySelector('[data-fabric-preview-popover]');
      this.previewPopoverImg = this.mount.querySelector('[data-fabric-preview-image]');
      this.previewPopoverLabel = this.mount.querySelector('[data-fabric-preview-label]');
    }

    initializeSelections() {
      this.options.forEach((option) => {
        if (!option.values.length) return;
        const preferred = this.getPreferredDefaultValue(option);
        if (preferred) this.selectedValues.set(String(option.id), preferred);
      });

      this.syncSelectionUI();
    }

    syncSelectionUI() {
      this.options.forEach((option) => {
        const selected = this.selectedValues.get(String(option.id));
        const buttons = this.mount.querySelectorAll('[data-option-id="' + CSS.escape(String(option.id)) + '"]');

        buttons.forEach((button) => {
          const isActive = selected && String(button.dataset.valueId) === String(selected.id);
          button.classList.toggle('is-selected', isActive);
          button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });
      });
    }

    bindEvents() {
      this.mount.addEventListener('click', (event) => {
        const closePreviewBtn = event.target.closest('[data-fabric-preview-close]');
        if (closePreviewBtn) {
          event.preventDefault();
          event.stopPropagation();
          this.closePreviewPopover();
          return;
        }


        const samplesButton = event.target.closest('[data-open-samples]');
        if (samplesButton) {
          event.preventDefault();
          event.stopPropagation();
          window.open(samplesButton.dataset.openSamples, '_blank');
          return;
        }

        const conformButton = event.target.closest('[data-open-conform-configurator]');
        if (conformButton) {
          event.preventDefault();
          event.stopPropagation();
          window.open(this.getShopifyPageUrl('/pages/conform-configurator'), '_blank');
          return;
        }

        const swatchButton = event.target.closest('.fabric-configurator__swatch');
        if (swatchButton) {
          event.preventDefault();
          this.handleSwatchClick(swatchButton);
          this.togglePreviewPopover(swatchButton);
        }
      });

      document.addEventListener('click', (event) => {
        if (!this.previewPopover || this.previewPopover.hidden) return;
        const clickedInsidePopover = event.target.closest('[data-fabric-preview-popover]');
        const clickedSwatch = event.target.closest('.fabric-configurator__swatch');
        if (!clickedInsidePopover && !clickedSwatch) this.closePreviewPopover();
      });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          if (this.previewPopover && !this.previewPopover.hidden) this.closePreviewPopover();
          if (this.modal && !this.modal.hidden) this.closeModal();
        }
      });

      if (this.modal) {
        this.modal.addEventListener('click', (event) => {
          if (event.target.closest('[data-fabric-modal-close]')) {
            event.preventDefault();
            this.closeModal();
          }
        });
      }

      this.form.addEventListener('submit', (event) => this.handleSubmit(event));
      this.form.addEventListener('change', () => this.updateDisplayedTotals());

      const quantityInput = this.getQuantityInput();
      if (quantityInput) {
        quantityInput.addEventListener('input', () => this.updateDisplayedTotals());
        quantityInput.addEventListener('change', () => this.updateDisplayedTotals());
      }

      document.addEventListener('variant:change', () => this.updateDisplayedTotals());
      document.addEventListener('product:variant-change', () => this.updateDisplayedTotals());

      if (this.variantInput) {
        const observer = new MutationObserver(() => this.updateDisplayedTotals());
        observer.observe(this.variantInput, { attributes: true, attributeFilter: ['value'] });
      }
    }

    openModal(url) {
      if (!this.modal || !this.modalFrame || !url) return;

      this.closePreviewPopover();

      if (!this.modal.dataset.movedToBody) {
        document.body.appendChild(this.modal);
        this.modal.dataset.movedToBody = 'true';
      }

      this.modal.hidden = false;
      this.modalFrame.src = url;

      document.documentElement.classList.add('fabric-configurator-modal-open');
      document.body.classList.add('fabric-configurator-modal-open');
    }

    closeModal() {
      if (!this.modal || !this.modalFrame) return;

      this.modal.hidden = true;
      this.modalFrame.src='about:blank';

      document.documentElement.classList.remove('fabric-configurator-modal-open');
      document.body.classList.remove('fabric-configurator-modal-open');
    }

    handleSwatchClick(button) {
      const optionId = String(button.dataset.optionId);
      const valueId = String(button.dataset.valueId);
      const option = this.options.find((item) => String(item.id) === optionId);
      if (!option) return;

      const value = option.values.find((item) => String(item.id) === valueId);
      if (!value) return;

      this.selectedValues.set(optionId, value);
      this.syncSelectionUI();
      this.updatePropertiesInputs();
      this.updateDisplayedTotals();
    }

    togglePreviewPopover(button) {
      const image = this.normalizeFabricImageUrl(button.dataset.previewImage || '');
      const label = button.dataset.previewLabel || '';

      if (!image || !this.previewPopover || !this.previewPopoverImg || !this.previewPopoverLabel) {
        this.closePreviewPopover();
        return;
      }

      if (!this.previewPopover.hidden && this.activePreviewButton === button) {
        this.closePreviewPopover();
        return;
      }

      this.activePreviewButton = button;
      this.previewPopoverImg.src = image;
      this.previewPopoverImg.alt = label;
      this.previewPopoverLabel.textContent = label;
      this.previewPopover.hidden = false;

      const rootRect = this.root.getBoundingClientRect();
      const btnRect = button.getBoundingClientRect();
      const popoverWidth = 270;
      const popoverHeight = 310;
      const margin = 10;

      let left = btnRect.left - rootRect.left + btnRect.width / 2 - popoverWidth / 2;
      let top = btnRect.bottom - rootRect.top + margin;

      const maxLeft = Math.max(0, rootRect.width - popoverWidth - 8);
      if (left < 0) left = 0;
      if (left > maxLeft) left = maxLeft;

      const estimatedBottom = top + popoverHeight;
      if (estimatedBottom > this.root.offsetHeight + 120) top = btnRect.top - rootRect.top - popoverHeight - margin;
      if (top < 0) top = 0;

      this.previewPopover.style.left = `${left}px`;
      this.previewPopover.style.top = `${top}px`;
    }

    closePreviewPopover() {
      if (!this.previewPopover) return;
      this.previewPopover.hidden = true;
      this.previewPopover.style.left = '';
      this.previewPopover.style.top = '';
      this.activePreviewButton = null;
    }

    getPropertiesInputs() {
      if (!this.propertiesTarget) {
        return {
          fabricInput: null,
          surchargeLabelInput: null,
          surchargeCentsInput: null
        };
      }

      return {
        fabricInput: this.propertiesTarget.querySelector('[data-fabric-property]'),
        surchargeLabelInput: this.propertiesTarget.querySelector('[data-fabric-surcharge-property]'),
        surchargeCentsInput: this.propertiesTarget.querySelector('[data-fabric-surcharge-cents-property]')
      };
    }

    updatePropertiesInputs() {
      const { fabricInput, surchargeLabelInput, surchargeCentsInput } = this.getPropertiesInputs();

      const selections = [];
      const surchargeParts = [];
      let surchargeCentsTotal = 0;

      this.options.forEach((option) => {
        const selected = this.selectedValues.get(String(option.id));
        if (!selected) return;

        let valueLabel = `${option.name}: ${selected.label}`;
        if (selected.surcharge > 0) {
          valueLabel += ` (${this.formatEuroBadge(selected.surcharge)})`;
          surchargeParts.push(`${option.name}: ${this.formatEuroBadge(selected.surcharge)}`);
          surchargeCentsTotal += selected.surchargeCents;
        }
        selections.push(valueLabel);
      });

      if (fabricInput) fabricInput.value = selections.join(' | ');
      if (surchargeLabelInput) surchargeLabelInput.value = surchargeParts.join(' | ');
      if (surchargeCentsInput) surchargeCentsInput.value = surchargeCentsTotal > 0 ? String(surchargeCentsTotal) : '';
    }

    getCurrentVariantId() {
      if (!this.variantInput) return null;
      return String(this.variantInput.value || '').trim() || null;
    }

    getCurrentVariant() {
      const variantId = this.getCurrentVariantId();
      if (!variantId) return null;
      return this.variantsById.get(String(variantId)) || null;
    }

    getBasePriceCents() {
      const currentVariant = this.getCurrentVariant();
      if (currentVariant && typeof currentVariant.price === 'number') return currentVariant.price;
      return 0;
    }

    getQuantityInput() {
      if (!this.form) return null;
      return this.form.querySelector('input[name="quantity"]') || Array.from(this.form.elements).find(el => el.name === 'quantity') || null;
    }

    getQuantity() {
      const input = this.getQuantityInput();
      if (!input) return 1;

      const quantity = parseInt(input.value, 10);
      return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
    }

    getSelectedDisplaySurchargeTotalCents() {
      let sum = 0;

      this.options.forEach((option) => {
        const selected = this.selectedValues.get(String(option.id));
        if (!selected || selected.surchargeCents <= 0) return;
        sum += selected.surchargeCents;
      });

      return sum;
    }

    getSelectedSurchargeLines() {
      const lines = [];

      this.options.forEach((option) => {
        const selected = this.selectedValues.get(String(option.id));
        if (!selected || !selected.surchargeVariantId || selected.surchargeCents <= 0) {
          if (selected && selected.surchargeCents > 0 && !selected.surchargeVariantId) {
            console.warn('Missing surcharge variant mapping for cents:', selected.surchargeCents, 'label:', selected.label);
          }
          return;
        }

        lines.push({
          optionName: option.name,
          label: selected.label,
          surcharge: selected.surcharge,
          surchargeCents: selected.surchargeCents,
          variantId: selected.surchargeVariantId
        });
      });

      return lines;
    }

    updateDisplayedTotals() {
      const totalCents =
        (this.getBasePriceCents() + this.getSelectedDisplaySurchargeTotalCents()) * this.getQuantity();

      const configuratorTotalEl = this.root.querySelector('[data-configurator-total-price]');
      if (configuratorTotalEl) configuratorTotalEl.textContent = this.formatMoneyFromCents(totalCents);

      const visibleTarget = (document.getElementById('MainProduct-' + this.sectionId) || this.root.closest('.product') || document).querySelector('[data-selected-fabric-total-target]');
      if (visibleTarget) visibleTarget.textContent = this.formatMoneyFromCents(totalCents);
    }

    formatEuroBadge(amount) {
      return `+${Number(amount).toFixed(2)}€`;
    }

    formatMoneyFromCents(cents) {
      const amount = Number(cents || 0) / 100;
      try {
        return new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: this.currencyCode
        }).format(amount);
      } catch (error) {
        return `${amount.toFixed(2)} ${this.currencyCode}`;
      }
    }

    setLoadingState(isLoading) {
      if (!this.submitButton) return;
      this.submitButton.disabled = isLoading;
      this.submitButton.classList.toggle('is-loading', isLoading);
      this.submitButton.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    }

    showError(message) {
      const wrapper = this.productForm?.querySelector('.product-form__error-message-wrapper');
      const messageEl = wrapper?.querySelector('.product-form__error-message');

      if (wrapper && messageEl) {
        messageEl.textContent = message;
        wrapper.hidden = false;
      } else {
        alert(message);
      }
    }

    hideError() {
      const wrapper = this.productForm?.querySelector('.product-form__error-message-wrapper');
      const messageEl = wrapper?.querySelector('.product-form__error-message');

      if (wrapper && messageEl) {
        messageEl.textContent = '';
        wrapper.hidden = true;
      }
    }

    buildMainLineItem(mainVariantId, qty, groupId) {
      const { fabricInput, surchargeLabelInput, surchargeCentsInput } = this.getPropertiesInputs();
      const properties = {};

      if (fabricInput && fabricInput.value) properties['Configuration'] = fabricInput.value;
      if (surchargeLabelInput && surchargeLabelInput.value) properties['Option surcharge'] = surchargeLabelInput.value;
      if (surchargeCentsInput && surchargeCentsInput.value) properties['_Option surcharge cents'] = surchargeCentsInput.value;
      properties['_Configurator group'] = groupId;

      return {
        id: Number(mainVariantId),
        quantity: qty,
        properties
      };
    }

    buildSurchargeLineItems(mainVariantId, qty, groupId) {
      const surchargeLines = this.getSelectedSurchargeLines();
      const aggregated = new Map();

      surchargeLines.forEach((line) => {
        const key = String(line.variantId);
        const existing = aggregated.get(key);

        if (existing) {
          existing.quantity += qty;
          existing.properties['_Units per product'] = String(Number(existing.properties['_Units per product']) + 1);
        } else {
          aggregated.set(key, {
            id: Number(line.variantId),
            quantity: qty,
            properties: {
              '_Configurator group': groupId,
              '_Units per product': '1',
              '_Linked product': String(this.productId),
              '_Linked main variant': String(mainVariantId),
              '_Option detail': `${line.optionName}: ${line.label}`,
              '_Surcharge': this.formatEuroBadge(line.surcharge)
            }
          });
        }
      });

      return Array.from(aggregated.values());
    }

    async handleSubmit(event) {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (this.submitting) return;
      this.hideError();
      this.updatePropertiesInputs();

      const mainVariantId = this.getCurrentVariantId();
      if (!mainVariantId) {
        this.showError('Please select a valid product variant.');
        return;
      }

      const unsupported = Array.from(this.selectedValues.values()).some(value => value.surchargeCents > 0 && !value.surchargeVariantId);
      if (unsupported) { this.showError(this.labels.missing_option || 'Please contact us to confirm pricing for this option before ordering.'); return; }
      const qty = this.getQuantity();
      const groupId = `cfg-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

      const items = [
        this.buildMainLineItem(mainVariantId, qty, groupId),
        ...this.buildSurchargeLineItems(mainVariantId, qty, groupId)
      ];

      this.submitting = true;
      this.setLoadingState(true);

      try {
        const response = await fetch((window.Shopify?.routes?.root || '/') + 'cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({ items })
        });

        const data = await response.json();

        if (!response.ok) {
          const message = data?.description || data?.message || 'Unable to add the selected product to cart.';
          throw new Error(message);
        }

        window.location.href = this.cartUrl || '/cart';
      } catch (error) {
        console.error('Fabric configurator add-to-cart failed:', error);
        this.showError(error.message || 'Something went wrong while adding this product to cart.');
      } finally {
        this.submitting = false;
        this.setLoadingState(false);
      }
    }
  }

  function initFabricConfigurators() {
    const roots = document.querySelectorAll('[data-fabric-configurator]:not([data-initialized="true"])');
    roots.forEach((root) => {
      const instance = new FabricConfigurator(root);
      instance.init();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initFabricConfigurators);
  } else {
    initFabricConfigurators();
  }

  document.addEventListener('shopify:section:load', initFabricConfigurators);
})();
